/**
 * @fileoverview React Query hook for fetching integration status.
 * @module features/settings/hooks/useIntegrationsStatus
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import {
  getIntegrationsStatus,
  INTEGRATIONS_STATUS_QUERY_KEY,
} from '../api/integrationStatusService'
import type { IntegrationStatus } from '../schemas/integrationStatusSchema'

/**
 * Hook to fetch integration status for all providers.
 *
 * @returns Query result with array of integration statuses
 */
export function useIntegrationsStatus(): UseQueryResult<IntegrationStatus[], Error> {
  const { activeOrgId } = useActiveOrg()

  return useQuery({
    queryKey: activeOrgId
      ? ['org', activeOrgId, ...INTEGRATIONS_STATUS_QUERY_KEY]
      : INTEGRATIONS_STATUS_QUERY_KEY,
    queryFn: () => {
      if (!activeOrgId) {
        throw new Error('No active organization')
      }
      return getIntegrationsStatus(activeOrgId)
    },
    enabled: !!activeOrgId,
    staleTime: 60_000, // 1 minute
  })
}
